import React from 'react';

import { UseFormRegisterReturn } from 'react-hook-form';
import styled, { css } from 'styled-components';
import useTracking, { TrackingEventData } from '../../../hooks/useTracking';
import { BACKGROUND, BORDER_GRAY, ICON_SECONDARY } from '../../../theme';
import { EventNames } from '../../../utils/trackingEvents';
import Errors from '../ErrorMessage';
import Text, { TextColor, TextKind, textStyleFrom } from '../Text';

const StyledInputContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledLabelContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const TextInputFontStyles = textStyleFrom(TextKind.BodyM, TextColor.Primary);

const StyledTextArea = styled.textarea<{
  $height?: string;
  $backgroundColor?: string;
  $resizable?: boolean;
}>`
  padding: 8px;
  border: 1px solid ${BORDER_GRAY};
  border-radius: 8px;
  background-color: ${({ $backgroundColor }) => $backgroundColor ?? BACKGROUND};
  overflow-y: auto;
  min-height: 70px;
  ${({ $height }) => css`
    height: ${$height ?? 'auto'};
  `}
  resize: ${({ $resizable }) => ($resizable ? 'vertical' : 'none')};

  & {
    ${{ ...TextInputFontStyles }}
    line-height: 20px;
  }
  &::placeholder,
  &::-webkit-input-placeholder {
    color: ${ICON_SECONDARY};
    opacity: 1;
  }
`;

interface InputProps {
  label?: string;
  // default placeholder is the label
  placeholder?: string;
  // used for `useForm` register util
  formRegister?: UseFormRegisterReturn;
  onFocus?: () => void;
  autoFocus?: boolean;
  type?: string;
  height?: string;
  optional?: boolean;
  id?: string;
  trackingData?: TrackingEventData;
  maxLength?: number;
  backgroundColor?: string;
  resizable?: boolean;
}

const TextAreaInput: React.FC<InputProps> = ({
  formRegister,
  label,
  placeholder,
  onFocus,
  autoFocus,
  optional,
  id,
  trackingData,
  maxLength,
  height,
  backgroundColor,
  resizable,
}) => {
  const { track } = useTracking();
  const handleFocus = () => {
    if (trackingData && formRegister?.name) {
      track(EventNames.TextBoxActivated, trackingData);
    }
  };

  return (
    <StyledInputContainer>
      {label && (
        <StyledLabelContainer>
          <Text component="label" kind={TextKind.LabelL} htmlFor={id}>
            {label}
          </Text>
          {optional && (
            <Text
              component="label"
              kind={TextKind.BodyS}
              color={TextColor.Secondary}
            >
              (Optional)
            </Text>
          )}
        </StyledLabelContainer>
      )}
      <StyledTextArea
        id={id}
        data-testid={id}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onFocus={onFocus}
        onFocusCapture={handleFocus}
        maxLength={maxLength}
        $height={height}
        $backgroundColor={backgroundColor}
        $resizable={resizable}
        {...formRegister}
      />
      {formRegister?.name && (
        <Errors name={formRegister.name} errorTextKind={TextKind.BodyS} />
      )}
    </StyledInputContainer>
  );
};

export default TextAreaInput;
